import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Search, Building2, Plus, Check } from 'lucide-react';
import { Modal, Button, toast } from '../../components/common';
import { vendorApi, rfqApi } from '../../services/api';
import './VendorInviteModal.css';

export default function VendorInviteModal({ rfqId, isOpen, onClose, onSuccess, excludeVendorIds = [] }) {
    const [search, setSearch] = useState('');
    const [selectedVendors, setSelectedVendors] = useState([]);

    const { data: vendors, isLoading } = useQuery({
        queryKey: ['vendors', 'active', search],
        queryFn: () => vendorApi.list({ status: 'active', search, limit: 50 }),
        select: (res) => res.data?.data || [],
        enabled: isOpen
    });

    const inviteMutation = useMutation({
        mutationFn: async (vendorIds) => {
            // Backend only supports inviting one vendor at a time
            const results = [];
            for (const vendorId of vendorIds) {
                try {
                    const result = await rfqApi.inviteVendor(rfqId, { vendorId });
                    results.push(result);
                } catch (err) {
                    console.error(`Failed to invite vendor ${vendorId}:`, err);
                    throw err;
                }
            }
            return results;
        },
        onSuccess: () => {
            toast.success('Vendors invited successfully');
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to invite vendors');
        }
    });

    const filteredVendors = vendors?.filter(v => !excludeVendorIds.includes(v.id)) || [];

    const toggleVendor = (vendorId) => {
        setSelectedVendors(prev =>
            prev.includes(vendorId)
                ? prev.filter(id => id !== vendorId)
                : [...prev, vendorId]
        );
    };

    const handleInvite = () => {
        if (selectedVendors.length === 0) {
            toast.error('Please select at least one vendor');
            return;
        }
        inviteMutation.mutate(selectedVendors);
    };

    const handleClose = () => {
        setSelectedVendors([]);
        setSearch('');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Invite Vendors"
            size="md"
        >
            <div className="vendor-invite">
                <div className="invite-search">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search active vendors..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="vendor-list">
                    {isLoading ? (
                        <div className="vendor-loading">Loading vendors...</div>
                    ) : filteredVendors.length === 0 ? (
                        <div className="vendor-empty">
                            <Building2 size={36} />
                            <p>No active vendors found</p>
                        </div>
                    ) : (
                        filteredVendors.map(vendor => (
                            <div
                                key={vendor.id}
                                className={`vendor-item ${selectedVendors.includes(vendor.id) ? 'selected' : ''}`}
                                onClick={() => toggleVendor(vendor.id)}
                            >
                                <div className="vendor-icon">
                                    <Building2 size={18} />
                                </div>
                                <div className="vendor-info">
                                    <span className="vendor-name">{vendor.companyName}</span>
                                    <span className="vendor-email">{vendor.email}</span>
                                </div>
                                <div className="vendor-check">
                                    {selectedVendors.includes(vendor.id) ? (
                                        <Check size={18} />
                                    ) : (
                                        <Plus size={18} />
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="invite-footer">
                    <span className="selected-count">
                        {selectedVendors.length} vendor(s) selected
                    </span>
                    <div className="invite-actions">
                        <Button variant="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleInvite}
                            loading={inviteMutation.isPending}
                            disabled={selectedVendors.length === 0}
                        >
                            Invite Selected
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

VendorInviteModal.propTypes = {
    rfqId: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    excludeVendorIds: PropTypes.array
};
